// The 14-stage palette from lendsure-pipeline:docs/research/12-ux-design.md §2.3 (light + dark, AA-checked there).
// Labels and order come from /api/me `stages`; colours come from here (server colours are a fallback).
import type { Stage } from "./types";
import { stageDisplayAbbr } from "./loanSteps";

export interface StageTone {
  solid: string;
  on: string;
  tint: string;
}

export interface StageToken {
  abbr: string;
  light: StageTone;
  dark: StageTone;
}

const INK = "#14202E";
const N950 = "#0A1C38";
const W = "#FFFFFF";

export const STAGE_TOKENS: Record<string, StageToken> = {
  scenario: { abbr: "Scenario", light: { solid: "#E9D5FF", on: INK, tint: "#FAF5FF" }, dark: { solid: "#C084FC", on: N950, tint: "#2E1F47" } },
  pq_triage: { abbr: "PQ Triage", light: { solid: "#BAE6FD", on: INK, tint: "#F0F9FF" }, dark: { solid: "#7DD3FC", on: N950, tint: "#18334A" } },
  pq_analysis: { abbr: "PQ Analysis", light: { solid: "#FDE68A", on: INK, tint: "#FFFBEB" }, dark: { solid: "#FCD34D", on: N950, tint: "#35332A" } },
  pq_review: { abbr: "PQ Review", light: { solid: "#FCD34D", on: INK, tint: "#FEF3C7" }, dark: { solid: "#FBBF24", on: N950, tint: "#363126" } },
  pre_approved: { abbr: "PQ Ready", light: { solid: "#F2DF3A", on: INK, tint: "#FDFADF" }, dark: { solid: "#E2DD5A", on: N950, tint: "#323C38" } },
  le_requested: { abbr: "LE Req", light: { solid: "#F7C59F", on: INK, tint: "#FEF6F0" }, dark: { solid: "#F2B98E", on: N950, tint: "#353640" } },
  le_sent: { abbr: "LE Sent", light: { solid: "#EE9350", on: INK, tint: "#FCEEE3" }, dark: { solid: "#F0A066", on: N950, tint: "#353239" } },
  le_signed: { abbr: "LE Sgn", light: { solid: "#B5531A", on: W, tint: "#F3E3DA" }, dark: { solid: "#E07A3E", on: N950, tint: "#322C33" } },
  submitted_to_uw: { abbr: "UW", light: { solid: "#B9B6B7", on: INK, tint: "#F4F3F3" }, dark: { solid: "#A9A6A8", on: N950, tint: "#293344" } },
  cla_issued: { abbr: "CLA", light: { solid: "#7FDCD6", on: INK, tint: "#EBF9F8" }, dark: { solid: "#5FCFC8", on: N950, tint: "#1D3949" } },
  cd_requested: { abbr: "CD Req", light: { solid: "#FFE08A", on: INK, tint: "#FFFAEC" }, dark: { solid: "#F7CC7A", on: N950, tint: "#36393D" } },
  cd_sent: { abbr: "CD Sent", light: { solid: "#F2B705", on: INK, tint: "#FDF3D7" }, dark: { solid: "#E8B21A", on: N950, tint: "#33352D" } },
  cd_signed: { abbr: "CD Sgn", light: { solid: "#B88400", on: INK, tint: "#F4EBD6" }, dark: { solid: "#C2851C", on: N950, tint: "#2D2E2E" } },
  ctc_requested: { abbr: "CTC Req", light: { solid: "#9ED6F2", on: INK, tint: "#EFF8FD" }, dark: { solid: "#A3DDF5", on: N950, tint: "#283C50" } },
  ctc: { abbr: "CTC", light: { solid: "#3AA6DD", on: INK, tint: "#DFF1FA" }, dark: { solid: "#3FBCE6", on: N950, tint: "#18364E" } },
  ctc_docs_out: { abbr: "CTC–DO", light: { solid: "#1F6FB8", on: W, tint: "#DBE8F4" }, dark: { solid: "#5E95E6", on: N950, tint: "#1D304E" } },
  ctc_docs_signed: { abbr: "CTC–DS", light: { solid: "#16396B", on: W, tint: "#DADFE7" }, dark: { solid: "#A9B9F2", on: N950, tint: "#293650" } },
  funded: { abbr: "Funded", light: { solid: "#65AF00", on: INK, tint: "#E6F2D6" }, dark: { solid: "#7CC22A", on: N950, tint: "#223730" } },
};

export function stageTone(stage: Stage | undefined, dark: boolean): StageTone | null {
  if (!stage) return null;
  const t = STAGE_TOKENS[stage.key];
  if (t) return dark ? t.dark : t.light;
  return { solid: stage.color, on: stage.text_color, tint: stage.color };
}

export function stageAbbr(stage: Stage): string {
  return stageDisplayAbbr(stage) ?? STAGE_TOKENS[stage.key]?.abbr ?? stage.label;
}

/** Fuzzy stage input: label, key, abbreviation, prefix, or the 1-based step number. */
export function matchStage(input: string, stages: Stage[]): Stage | null | undefined {
  const s = input.trim().toLowerCase().replace(/[–—]/g, "-").replace(/\s+/g, " ");
  if (s === "" || s === "-") return null;
  if (/^\d{1,2}$/.test(s)) return stages.find((st) => st.sort === Number(s));
  const norm = (x: string) => x.toLowerCase().replace(/[–—]/g, "-").replace(/[^a-z0-9]/g, "");
  const ns = norm(s);
  return (
    stages.find((st) => norm(st.label) === ns || norm(st.key) === ns) ??
    stages.find((st) => norm(STAGE_TOKENS[st.key]?.abbr ?? "") === ns) ??
    stages.find((st) => {
      // "cds" → CD Sent, "docs out" → CTC - Docs Out
      const words = st.label.toLowerCase().replace(/[–—-]/g, " ").split(/\s+/).filter(Boolean);
      const acronym = words.map((w) => (w.length <= 3 ? w : w[0])).join("");
      return acronym === ns || words.map((w) => w[0]).join("") === ns;
    }) ??
    stages.find((st) => norm(st.label).includes(ns))
  );
}

export const FORECASTS = ["PIN", "MONEYBALL", "HIGH", "TBD"] as const;
export const FORECAST_LABEL: Record<string, string> = { PIN: "PIN", MONEYBALL: "MB", HIGH: "High", TBD: "TBD" };
export const FORECAST_LONG: Record<string, string> = { PIN: "PIN", MONEYBALL: "Moneyball", HIGH: "High", TBD: "TBD" };

export function matchForecast(input: string): string | null | undefined {
  const s = input.trim().toLowerCase();
  if (s === "" || s === "-") return null;
  if (["p", "pin", "pinned"].includes(s)) return "PIN";
  if (["m", "mb", "moneyball", "money ball"].includes(s)) return "MONEYBALL";
  if (["h", "high"].includes(s)) return "HIGH";
  if (["t", "tbd"].includes(s)) return "TBD";
  return undefined;
}

export const LOCK_LABEL: Record<string, string> = { floating: "Floating", locked: "Locked", n_a: "N/A" };

export function matchLock(input: string): string | null | undefined {
  const s = input.trim().toLowerCase();
  if (s === "" || s === "-") return null;
  if (s.startsWith("float")) return "floating";
  if (s.startsWith("lock")) return "locked";
  if (["n/a", "na", "n_a", "none"].includes(s)) return "n_a";
  return undefined;
}

export const OUTCOME_LABEL: Record<string, string> = {
  active: "Active",
  withdrawn: "Withdrawn",
  denied: "Denied",
  on_hold: "On hold",
};
